import React from 'react'

import bearing from '@turf/bearing'
import bezierSpline from '@turf/bezier-spline'
import destination from '@turf/destination'
import distance from '@turf/distance'
import midpoint from '@turf/midpoint'
import { useSelector } from 'react-redux'
import linestring from 'turf-linestring'
import { selectCurrentHistoryRoute } from '../../store/history/history.selectors'
import { selectCurrentProvider } from '../../store/itinerary/itinerary.selectors'
import { selectCurrentRouteId } from '../../store/itinerary/routes.selectors'
import { MINIMUM_DISTANCE_TO_SHOW_GAP_POLYLINE_IN_KM } from '../../store/map/map.constants'
import { selectIsMapReady, selectPolylineBeforeLayerId } from '../../store/map/map.selectors'
import { useEffectWithMapContext } from '../map/MapContext'
import { selectArrivalStep, selectDepartureStep } from './steps/steps.selectors'

export const getPolylineId = (routeId, sectionIdx, featureIdx) =>
  `polyline_route_${routeId}_section_${sectionIdx}_feature_${featureIdx}`

export const getPaintStyle = (isSelectedRoute, hasTraffic, feature) => {
  const style = feature?.properties?.display_style ?? 'solid'
  if (isSelectedRoute)
    return {
      'line-width': 7,
      'line-opacity': 0.8,
      'line-color': ['get', 'color'],
      ...(isSelectedRoute && (style === 'dotted' || style === 'dashed') ? { 'line-dasharray': [1.25, 0.5] } : {})
    }
  if (hasTraffic)
    return {
      'line-width': 5,
      'line-opacity': 1,
      'line-color': [
        'let',
        'rgba',
        ['to-rgba', ['to-color', ['get', 'color']]],
        [
          'let',
          'r',
          ['number', ['at', 0, ['var', 'rgba']]],
          'g',
          ['number', ['at', 1, ['var', 'rgba']]],
          'b',
          ['number', ['at', 2, ['var', 'rgba']]],
          'a',
          ['number', ['at', 3, ['var', 'rgba']]],
          [
            'let',
            'avg',
            ['+', ['*', ['var', 'r'], 0.299], ['*', ['var', 'g'], 0.587], ['*', ['var', 'b'], 0.114]],
            ['rgba', ['var', 'avg'], ['var', 'avg'], ['var', 'avg'], ['var', 'a']]
          ]
        ]
      ]
    }

  return {
    'line-width': 5,
    'line-opacity': 1,
    'line-color': '#CCCCCC'
  }
}

const renderOrUpdatePolyline = (map, polyline, beforeLayerId) => {
  const { routeId, sections = [], isSelectedRoute, hasTraffic } = polyline
  sections.forEach((section, sectionIdx) => {
    section.features.forEach((feature, featureIdx) => {
      const id = getPolylineId(routeId, sectionIdx, featureIdx)
      const paint = getPaintStyle(isSelectedRoute, hasTraffic, feature)
      try {
        if (!map.getLayer(id)) {
          map.addLayer(
            {
              id,
              type: 'line',
              source: {
                type: 'geojson',
                data: {
                  type: 'FeatureCollection',
                  features: [feature]
                }
              },
              paint
            },
            beforeLayerId
          )
        } else {
          if (isSelectedRoute) {
            map.moveLayer(id, beforeLayerId)
          }
          const paintAttributes = ['line-width', 'line-opacity', 'line-color', 'line-dasharray']
          paintAttributes.forEach(attr => {
            if (paint[attr]) map.setPaintProperty(id, attr, paint[attr])
          })
        }
      } catch (error) {
        console.error('failed to renderOrUpdatePolyline', error)
      }
    })
  })
}

const removePolyline = (map, polyline) => {
  const { routeId, sections = [] } = polyline
  sections.forEach((section, sectionIdx) => {
    section.features.forEach((_, featureIdx) => {
      const id = getPolylineId(routeId, sectionIdx, featureIdx)
      try {
        if (map) {
          // map can be undefined (cf expense report route)
          if (map.getLayer(id)) map.removeLayer(id)
          if (map.getSource(id)) map.removeSource(id)
        }
      } catch (error) {
        console.error('failed to remove polyline', error)
      }
    })
  })
}

const renderGapPolyline = (map, id, start, end) => {
  const bearingValue = bearing(start, end)

  const leftSideArc = bearingValue + 90 > 180 ? -180 + (bearingValue + 90 - 180) : bearingValue + 90

  const distanceValue = distance(start, end)
  const midpointValue = midpoint(start, end)
  const destinationValue = destination(midpointValue, distanceValue / 5, leftSideArc)

  const curvedLine = bezierSpline(linestring([start, destinationValue.geometry.coordinates, end]))

  map.addLayer({
    id,
    type: 'line',
    source: {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [...curvedLine.geometry.coordinates]
        }
      }
    },
    layout: {
      'line-cap': 'round'
    },
    paint: {
      'line-color': 'black',
      'line-width': 5,
      'line-dasharray': [0, 2]
    }
  })
}

const removeGapPolyline = (map, id) => {
  try {
    if (map.getLayer(id)) map.removeLayer(id)
    if (map.getSource(id)) map.removeSource(id)
  } catch (error) {
    console.error('failed to removeGapPolyline', error)
  }
}

const Polyline = props => {
  const { polyline, polylineCount } = props
  const { isSelectedRoute } = polyline

  const currentProvider = useSelector(selectCurrentProvider)
  const isMapReady = useSelector(selectIsMapReady)
  const currentRouteId = useSelector(selectCurrentRouteId)
  const historyRoute = useSelector(selectCurrentHistoryRoute)
  const beforeLayerId = useSelector(selectPolylineBeforeLayerId)
  const arrivalStep = useSelector(selectArrivalStep)
  const departureStep = useSelector(selectDepartureStep)

  const firstSectionFeatureCoordinates = polyline?.sections?.[0].features?.[0].geometry.coordinates[0]

  const lastSection = polyline?.sections?.[polyline?.sections?.length - 1]
  const lastSectionFeature = lastSection?.features?.[lastSection?.features?.length - 1]
  const lastSectionFeatureCoordinates =
    lastSectionFeature?.geometry?.coordinates[lastSectionFeature?.geometry?.coordinates?.length - 1]

  useEffectWithMapContext(
    map => {
      if (!isMapReady) return
      if (isSelectedRoute && departureStep?.coordinates && arrivalStep?.coordinates) {
        const departureDistance = distance(
          [firstSectionFeatureCoordinates[0], firstSectionFeatureCoordinates[1]],
          [departureStep.coordinates.lng, departureStep.coordinates.lat]
        )

        const arrivalDistance = distance(
          [lastSectionFeatureCoordinates[0], lastSectionFeatureCoordinates[1]],
          [arrivalStep.coordinates.lng, arrivalStep.coordinates.lat]
        )
        if (departureDistance > MINIMUM_DISTANCE_TO_SHOW_GAP_POLYLINE_IN_KM) {
          const start = [parseFloat(firstSectionFeatureCoordinates[0]), parseFloat(firstSectionFeatureCoordinates[1])]
          const end = [parseFloat(departureStep.coordinates.lng), parseFloat(departureStep.coordinates.lat)]
          renderGapPolyline(map, 'departure-gap-polyline', start, end)
        }
        if (arrivalDistance > MINIMUM_DISTANCE_TO_SHOW_GAP_POLYLINE_IN_KM) {
          const start = [parseFloat(lastSectionFeatureCoordinates[0]), parseFloat(lastSectionFeatureCoordinates[1])]
          const end = [parseFloat(arrivalStep.coordinates.lng), parseFloat(arrivalStep.coordinates.lat)]
          renderGapPolyline(map, 'arrival-gap-polyline', start, end)
        }
      }
      if (!isSelectedRoute) {
        removeGapPolyline(map, 'departure-gap-polyline')
        removeGapPolyline(map, 'arrival-gap-polyline')
      }

      renderOrUpdatePolyline(map, polyline, beforeLayerId)
    },
    [currentProvider, currentRouteId, historyRoute, isSelectedRoute, polylineCount, isMapReady]
  )

  useEffectWithMapContext(
    map => () => {
      removeGapPolyline(map, 'departure-gap-polyline')
      removeGapPolyline(map, 'arrival-gap-polyline')
      removePolyline(map, polyline)
    },
    []
  )

  return <></>
}

// webpack:BrowserOnly
export default Polyline
